import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from 'react-router-dom';
import styled from 'styled-components';

import {
  AppBar,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MenuIcon from '@material-ui/icons/Menu';
import {
  FormTemplateDetailPage,
  FormTemplateListPage
} from '@sierralabs/forms-ui';

import logo from '../assets/logo';
import { CartProvider } from '../common/cart/CartContext';
import ProtectedRoute from '../common/ProtectedRoute';
import VersionInfo from '../common/VersionInfo';
import environment from '../environments';
import AuthGuard from '../guards/auth.guard';
import AdminGuard from '../guards/admin.guard';
import CapaAdminGuard from '../guards/capa-admin.guard';
import DocumentAdminGuard from '../guards/document-admin.guard';
import DocumentEditorGuard from '../guards/document-editor.guard';
import DocumentViewerGuard from '../guards/document-viewer.guard';
import HazardAdminGuard from '../guards/hazard-admin.guard';
import NoRoleGuard from '../guards/no-role.guard';
import ProductAdminGuard from '../guards/product-admin.guard';
import RiskAdminGuard from '../guards/risk-admin.guard';
import ScarAdminGuard from '../guards/scar-admin.guard';
import SiteAdminGuard from '../guards/site-admin.guard';
import WorkflowAdminGuard from '../guards/supplier-admin.guard';
import TraineeGuard from '../guards/trainee.guard';
import TrainingAdminGuard from '../guards/training-admin.guard';
import SupplierAdminGuard from '../guards/workflow-admin.guard';
import NavThemeProvider from '../NavThemeProvider';
import { defaultQmsFormElementPlugins } from '../plugins/form-elements/default';
import { UserContext } from '../user/User.context';
import { useLogout } from '../user/User.hooks';
import DashboardPage from './dashboard/DashboardPage';
import AdminDashboardPage from './admin-dashboard/AdminDashboardPage';
import AgreementAdminDetailPage from './agreements/AgreementAdminDetailPage';
import AgreementAdminListPage from './agreements/AgreementAdminListPage';
import AuditPage from './audit/AuditPage';
import CapaDetailPage from './capa/CapaDetailPage';
import CapaPage from './capa/CapaListPage';
import DocumentFileExplorer from './documents/components/DocumentFileExplorer';
import DocumentDetailPage from './documents/DocumentDetailPage';
import DocumentListPage from './documents/DocumentListPage';
import GroupDetailPage from './groups/GroupDetailPage';
import GroupListPage from './groups/GroupListPage';
import HazardActionDetailPage from './hazards/HazardActionDetailPage';
import HazardCauseDetailPage from './hazards/HazardCauseDetailPage';
import HazardControlDetailPage from './hazards/HazardControlDetailPage';
import HazardPage from './hazards/HazardPage';
import { useJiraConnectAPI } from './jira-connect/JiraConnect.hooks';
import JiraListExportPage from './jira-connect/JiraListExportPage';
import JiraDetailPage from './jira/JiraDetailPage';
import JiraListPage from './jira/JiraListPage';
import NavigationList, { NavigationItem } from './NavigationList';
import OrganizationDetailPage from './organization/OrganizationDetailPage';
import ProductDesignInputDocumentPage from './products/document/ProductDesignInputDocumentPage';
import ProductDesignOutputDocumentPage from './products/document/ProductDesignOutputDocumentPage';
import ProductUserNeedDocumentPage from './products/document/ProductUserNeedDocumentPage';
import ProductValidationDocumentPage from './products/document/ProductValidationDocumentPage';
import ProductVerificationDocumentPage from './products/document/ProductVerificationDocumentPage';
import ProductDesignInputDetailPage from './products/ProductDesignInputDetailPage';
import ProductDesignOutputDetailPage from './products/ProductDesignOutputDetailPage';
import ProductDesignSpecDetailPage from './products/ProductDesignSpecDetailPage';
import ProductListPage from './products/ProductListPage';
import ProductPage from './products/ProductPage';
import ProductUserNeedDetailPage from './products/ProductUserNeedDetailPage';
import ProductValidationDetailPage from './products/ProductValidationDetailPage';
import ProductVerificationDetailPage from './products/ProductVerificationDetailPage';
import PurchaseCreatePage from './purchase/PurchaseCreatePage';
import SporDetailPage from './purchase/SporDetailPage';
import QualityRoadmapPage from './quality-roadmap/QualityRoadmapPage';
import RiskDetailPage from './risk-management/RiskDetailPage';
import RiskManagementListPage from './risk-management/RiskManagementListPage';
import RoleListPage from './roles/RoleListPage';
import ScarDetailPage from './scar/ScarDetailPage';
import ScarPage from './scar/ScarListPage';
import SignupDetailPage from './signup/SignupDetailPage';
import SignupListPage from './signup/SignupListPage';
import SupplierDetailPage from './supplier/SupplierDetailPage';
import SupplierListPage from './supplier/SupplierListPage';
import TemplateListPage from './template/TemplateListPage';
import TrainingAdminBoard from './training/TrainingAdminBoard';
import TrainingAdminDetailPage from './training/TrainingAdminDetailPage';
import TrainingAdminListPage from './training/TrainingAdminListPage';
import TrainingAdminTrainees from './training/TrainingAdminTrainees';
import TrainingLessonPage from './training/TrainingLessonPage';
// import TrainingLogPage from './training/TrainingLogPage';
import TrainingPage from './training/TrainingPage';
import TraineeDetailPage from './training/TraineeDetailPage';
import UserDetailPage from './users/UserDetailPage';
import UserListPage from './users/UserListPage';
import UserProfilePage from './users/UserProfilePage';
import WorkflowDetailPage from './workflow/WorkflowDetailPage';
import WorkflowListPage from './workflow/WorkflowListPage';
import WorkflowV2DetailPage from './workflow-v2/WorkflowV2DetailPage';
import TaskTypeListPage from './task-types/TaskTypeListPage';
import TaskTypeDetailPage from './task-types/TaskTypeDetailPage';
import ChangeOrderListPage from './changeOrder/ChangeOrderListPage';
import ChangeOrderDetailPage from './changeOrder/ChangeOrderDetailPage';
import ChangeOrderAdminGuard from '../guards/change-order-admin.guard';

const StyledSecurePortal = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  nav {
    display: flex;
    flex-direction: column;
    z-index: 1;
    &.nav-close {
      transition-property: margin-left;
      transition-duration: ${props =>
        props.theme.transitions.duration.leavingScreen}ms;
      transition-timing-function: ${props =>
        props.theme.transitions.easing.sharp};
      margin-left: -260px;
      /* margin-left: -242px; */
    }
    &.nav-open {
      transition-property: margin-left;
      transition-duration: ${props =>
        props.theme.transitions.duration.enteringScreen}ms;
      transition-timing-function: ${props =>
        props.theme.transitions.easing.easeOut};
      margin-left: 0;
    }
    ${props => props.theme.breakpoints.down('md')} {
      &.nav-close {
        margin-left: 0;
      }
    }
  }
  main {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    z-index: 0;
  }
`;

const StyledDrawer = styled(Drawer)`
  width: 260px;
  .paper {
    width: 260px;
    background-color: #f1f3f3;
  }
`;

const StyledNavAppBar = styled(AppBar)``;

const StyledToolbar = styled(Toolbar)`
  padding-left: 0px;
  padding-right: 0px;
  align-items: flex-start;
  flex-direction: column;

  .content {
    width: 100%;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;

    .logo-container {
      width: 100%;
      padding-left: 24px;
      padding-right: 24px;

      .logo {
        display: block;
        margin: auto;
      }
    }
  }
`;

const StyledMainAppBar = styled(AppBar)`
  /* background-color: #fff; */
  .flex {
    flex: 1;
  }
`;

const StyledFileExplorerContainer = styled.div`
  padding: 4px 0 8px 12px;
`;

const StyledScrollDiv = styled.div`
  display: grid;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
`;

const useNavOpen = (
  isAboveMediumSize: boolean
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [isNavOpen, setIsNavOpen] = useState(isAboveMediumSize);
  const previousIsAboveMediumSizeRef = useRef(isAboveMediumSize);
  // only force nav open/close when a breakpoint change occurs
  if (isAboveMediumSize !== previousIsAboveMediumSizeRef.current) {
    if (!isNavOpen && isAboveMediumSize) {
      setIsNavOpen(true);
    } else if (isNavOpen && !isAboveMediumSize) {
      setIsNavOpen(false);
    }
  }
  useEffect(() => {
    // Store the previous breakpoint change
    previousIsAboveMediumSizeRef.current = isAboveMediumSize;
  }, [isAboveMediumSize]);

  return [isNavOpen, setIsNavOpen];
};

export interface SecurePortalProps {
  navigation?: React.ReactNode;
}

const SecurePortal: React.FC<SecurePortalProps> = props => {
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<null | HTMLElement>(null);
  const { user } = useContext(UserContext);
  const { doLogout } = useLogout();
  const history = useHistory();
  const location = useLocation();
  const { isAvailable: isInJira } = useJiraConnectAPI();
  const isAboveMediumSize = useMediaQuery<Theme>(
    theme => theme.breakpoints.up('lg'),
    { noSsr: true } // prevents 2 render calls on load
  );
  const [isNavOpen, setIsNavOpen] = useNavOpen(isAboveMediumSize);
  const showTreeview = location.pathname.startsWith('/portal/documents');
  const isOrganizationApiEnabled = environment.organizationConfig?.enabled;

  const handleDrawerToggle = () => {
    setIsNavOpen(!isNavOpen);
  };

  const handleClickLogout = () => {
    setProfileMenuAnchor(null);
    doLogout();
  };

  const handleNavigationItemClick = () => {
    if (!isAboveMediumSize) {
      setIsNavOpen(false);
    }
  };

  if (history.location.pathname.replace(/\//g, '') === 'portal') {
    // if navigating to /portal, redirect to training dashboard
    return <Redirect to='/portal/training' />;
  }
  return (
    <StyledSecurePortal>
      <CartProvider>
        <nav className={isNavOpen ? 'nav-open' : 'nav-close'}>
          <NavThemeProvider>
            <StyledDrawer
              variant={isAboveMediumSize ? 'persistent' : 'temporary'}
              anchor='left'
              open={isNavOpen}
              onClose={handleDrawerToggle}
              classes={{
                paper: 'paper'
              }}
              PaperProps={{ elevation: 10 }}
              ModalProps={{
                keepMounted: true // Better open performance on mobile.
              }}
            >
              <StyledNavAppBar position='static' elevation={1} square>
                <StyledToolbar>
                  <div className='content'>
                    <Typography variant='h6' noWrap className='logo-container'>
                      <img
                        src={logo}
                        className='logo'
                        alt='Sierra Labs QMS'
                        width={180}
                      />
                    </Typography>
                  </div>
                </StyledToolbar>
              </StyledNavAppBar>
              <StyledScrollDiv>
                {props.navigation || (
                  <NavigationList
                    onNavigationItemClicked={handleNavigationItemClick}
                    renderAfterItem={(item: NavigationItem) => {
                      return item.label === 'Documents' ? (
                        <StyledFileExplorerContainer
                          className='tree-thin'
                          style={
                            !showTreeview ? { display: 'none' } : undefined
                          }
                        >
                          <DocumentFileExplorer
                            hideRootDir
                            useContextValues
                            onSelectFile={(nodeId: string) => {
                              const id: number = parseInt(nodeId);
                              history.push(
                                id > 0
                                  ? `/portal/documents/folder/${id}`
                                  : '/portal/documents'
                              );
                            }}
                          />
                        </StyledFileExplorerContainer>
                      ) : null;
                    }}
                  />
                )}
                <VersionInfo />
              </StyledScrollDiv>
            </StyledDrawer>
          </NavThemeProvider>
        </nav>
        <main>
          {!isInJira && (
            <StyledMainAppBar position='static' color='secondary' square>
              <Toolbar>
                <IconButton
                  color='inherit'
                  aria-label='open drawer'
                  edge='start'
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
                <span className='flex'></span>
                <Button
                  className='user-button'
                  onClick={e => setProfileMenuAnchor(e.currentTarget)}
                >
                  {`${user?.firstName} ${user?.lastName}`}
                  <ArrowDropDownIcon />
                </Button>
                <Menu
                  id='profile-menu'
                  anchorEl={profileMenuAnchor}
                  keepMounted
                  open={Boolean(profileMenuAnchor)}
                  onClose={e => setProfileMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setProfileMenuAnchor(null);
                      history.push(`/portal/user-profile`);
                    }}
                  >
                    Profile
                  </MenuItem>
                  {isOrganizationApiEnabled && (
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        history.push('/portal/admin/organization');
                      }}
                    >
                      Organization
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleClickLogout}>Logout</MenuItem>
                </Menu>
              </Toolbar>
            </StyledMainAppBar>
          )}
          <Switch>
            <ProtectedRoute
              guards={[AuthGuard, NoRoleGuard]}
              exact
              path='/portal/dashboard/:view?'
              component={DashboardPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/admin-dashboard'
              component={AdminDashboardPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/quality-roadmap'
              component={QualityRoadmapPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/audit'
              component={AuditPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TraineeGuard]}
              exact
              path='/portal/training'
              component={TrainingPage}
            />
            {/* NOTE: unused and empty component */}
            {/* <ProtectedRoute
            guards={[SiteAdminGuard, TraineeGuard, TrainingAdminGuard]}
            exact
            path='/portal/training-log'
            component={TrainingLogPage}
          /> */}
            <ProtectedRoute
              guards={[
                AdminGuard,
                SiteAdminGuard,
                TraineeGuard,
                TrainingAdminGuard
              ]}
              exact
              path={[
                '/portal/training/:trainingId/version/:trainingVersionId',
                '/portal/training/:trainingId/version/:trainingVersionId/user/:userId'
              ]}
              component={TrainingLessonPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TrainingAdminGuard]}
              exact
              path='/portal/admin/training'
              component={TrainingAdminListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TrainingAdminGuard]}
              exact
              path='/portal/admin/trainees'
              component={TrainingAdminTrainees}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TrainingAdminGuard]}
              exact
              path='/portal/admin/trainee/:userId/detail'
              component={TraineeDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TrainingAdminGuard]}
              exact
              path='/portal/admin/training-board'
              component={TrainingAdminBoard}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, TrainingAdminGuard]}
              path='/portal/admin/training/task/:taskId'
              component={TrainingAdminDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, RiskAdminGuard]}
              exact
              path='/portal/risk'
              component={RiskManagementListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, RiskAdminGuard]}
              exact
              path='/portal/risks/task/:taskId'
              component={RiskDetailPage}
            />
            <ProtectedRoute
              guards={[
                AdminGuard,
                SiteAdminGuard,
                DocumentAdminGuard,
                DocumentEditorGuard,
                DocumentViewerGuard
              ]}
              exact
              path='/portal/documents'
              component={DocumentListPage}
            />
            <ProtectedRoute
              guards={[
                AdminGuard,
                SiteAdminGuard,
                DocumentAdminGuard,
                DocumentEditorGuard,
                DocumentViewerGuard
              ]}
              exact
              path='/portal/documents/folder/:folderId'
              component={DocumentListPage}
            />
            <ProtectedRoute
              guards={[
                AdminGuard,
                SiteAdminGuard,
                DocumentAdminGuard,
                DocumentEditorGuard,
                DocumentViewerGuard
              ]}
              exact
              path='/portal/documents/file/task/:taskId'
              component={DocumentDetailPage}
            />
            {/* <ProtectedRoute
            guards={[AdminGuard, DocumentEditorGuard]}
            path='/portal/document-board'
            component={DocumentBoard}
          /> */}
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products'
              component={ProductListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/user-needs'
              component={ProductUserNeedDocumentPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/user-needs/:userNeedId'
              component={ProductUserNeedDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/design-inputs'
              component={ProductDesignInputDocumentPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/design-inputs/:designInputId'
              component={ProductDesignInputDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/design-outputs'
              component={ProductDesignOutputDocumentPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/design-outputs/:designOutputId'
              component={ProductDesignOutputDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/verifications'
              component={ProductVerificationDocumentPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/verifications/:verificationId'
              component={ProductVerificationDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/validations'
              component={ProductValidationDocumentPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/validations/:validationId'
              component={ProductValidationDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/design-specs/:designSpecId'
              component={ProductDesignSpecDetailPage}
            />
            {/* avoid overriding Product document page routes */}
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ProductAdminGuard]}
              exact
              path='/portal/products/:productId/:view?'
              component={ProductPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, HazardAdminGuard]}
              exact
              path='/portal/products/:productId/hazards/:hazardId/:view?'
              component={HazardPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, HazardAdminGuard]}
              exact
              path='/portal/products/:productId/hazards/:hazardId/causes/:causeId'
              component={HazardCauseDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, HazardAdminGuard]}
              exact
              path='/portal/products/:productId/hazards/:hazardId/controls/:controlId'
              component={HazardControlDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, HazardAdminGuard]}
              exact
              path='/portal/products/:productId/hazards/:hazardId/actions/:actionId'
              component={HazardActionDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, CapaAdminGuard]}
              exact
              path='/portal/capa'
              component={CapaPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, CapaAdminGuard]}
              exact
              path='/portal/capa/task/:taskId'
              component={CapaDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ScarAdminGuard]}
              exact
              path='/portal/scar/'
              component={ScarPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ScarAdminGuard]}
              exact
              path='/portal/scar/task/:taskId'
              component={ScarDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ChangeOrderAdminGuard]}
              exact
              path='/portal/change-order/'
              component={ChangeOrderListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, ChangeOrderAdminGuard]}
              exact
              path='/portal/change-order/task/:taskId'
              component={ChangeOrderDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/users'
              component={UserListPage}
            />
            <Route
              exact
              path='/portal/user-profile'
              component={UserProfilePage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/users/:userId'
              component={UserDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/groups/:groupId'
              component={GroupDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/groups'
              component={GroupListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/roles'
              component={RoleListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, SupplierAdminGuard]}
              exact
              path='/portal/suppliers'
              component={SupplierListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, SupplierAdminGuard]}
              path='/portal/suppliers/task/:taskId'
              component={SupplierDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, SupplierAdminGuard]}
              exact
              path='/portal/spors/task/:taskId'
              component={SporDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard, SupplierAdminGuard]}
              exact
              path='/portal/purchase/form/:taskId'
              component={PurchaseCreatePage}
            />
            <ProtectedRoute
              guards={[AdminGuard, WorkflowAdminGuard]}
              exact
              path='/portal/admin/workflow'
              component={WorkflowListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, WorkflowAdminGuard]}
              path='/portal/admin/workflow/:workflowId'
              component={WorkflowDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, WorkflowAdminGuard]}
              path='/portal/admin/v2/workflows/:workflowId'
              component={WorkflowV2DetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, WorkflowAdminGuard]}
              exact
              path='/portal/admin/task-types'
              component={TaskTypeListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, WorkflowAdminGuard]}
              exact
              path='/portal/admin/task-types/:name'
              component={TaskTypeDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              path='/portal/admin/templates'
              component={TemplateListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/admin/agreements'
              component={AgreementAdminListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              path='/portal/admin/agreements/:agreementId'
              component={AgreementAdminDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard]}
              exact
              path='/portal/admin/form-templates'
              component={FormTemplateListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard]}
              path='/portal/admin/form-templates/:formTemplateId'
            >
              <FormTemplateDetailPage
                elementPlugins={defaultQmsFormElementPlugins}
              />
            </ProtectedRoute>
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/jira'
              component={JiraListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              path='/portal/jira/:issueId'
              component={JiraDetailPage}
            />
            <ProtectedRoute
              guards={[AdminGuard, SiteAdminGuard]}
              exact
              path='/portal/jira-connect'
              component={JiraListExportPage}
            />
            <ProtectedRoute
              guards={[AdminGuard]}
              exact
              path='/portal/admin/signups'
              component={SignupListPage}
            />
            <ProtectedRoute
              guards={[AdminGuard]}
              exact
              path='/portal/admin/signups/:id'
              component={SignupDetailPage}
            />
            {isOrganizationApiEnabled && (
              <ProtectedRoute
                guards={[AdminGuard, SiteAdminGuard]}
                exact
                path='/portal/admin/organization'
                component={OrganizationDetailPage}
              />
            )}
            {props.children}
          </Switch>
        </main>
      </CartProvider>
    </StyledSecurePortal>
  );
};

export default SecurePortal;
